import type { SearchResult } from '@/hooks/useSearch'
import type { FieldIntent } from '@/lib/search/fieldIntent'
import type { Task } from '@/types/task'
import type { Project } from '@/types/project'
import type { Contact } from '@/types/contact'

interface SearchResultItemProps {
  result: SearchResult
  isSelected: boolean
  onClick: () => void
  /** The field the active query is asking for — lets a phone-number subtitle render as a tap-to-call link. */
  intent?: FieldIntent | null
}

// The raw phone value behind this result, if the result's type carries one.
// Field names differ per type (Task/Project use phoneNumber, Contact uses
// phone), so this is the one place that has to know all three.
function phoneValueFor(result: SearchResult): string | undefined {
  switch (result.type) {
    case 'task':
      return (result.item as Task).phoneNumber
    case 'project':
      return (result.item as Project).phoneNumber
    case 'contact':
      return (result.item as Contact).phone
    default:
      return undefined
  }
}

export function SearchResultItem({ result, isSelected, onClick, intent = null }: SearchResultItemProps) {
  // Type-specific icon
  const renderIcon = () => {
    switch (result.type) {
      case 'task':
        return (
          <span
            className={`
              w-4 h-4 rounded border-2 flex items-center justify-center shrink-0
              ${result.completed
                ? 'bg-primary-500 border-primary-500 text-white'
                : 'border-neutral-300'
              }
            `}
          >
            {result.completed && (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-2.5 h-2.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </span>
        )
      case 'project':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-blue-500 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
          </svg>
        )
      case 'contact':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-primary-500 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
        )
      case 'routine':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-amber-500 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
          </svg>
        )
      case 'list':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-purple-500 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
          </svg>
        )
      case 'note':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-emerald-500 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
            <path d="M8 11a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1zM8 7a1 1 0 011-1h4a1 1 0 110 2H9a1 1 0 01-1-1z" />
          </svg>
        )
    }
  }

  // Under phone intent, when the subtitle IS the field value we just surfaced
  // (see useSearch), render it as a tap-to-call link — same tel: treatment as
  // everywhere else in the app (PanelReach, ContactCard, DetailPanelRedesign).
  // The exact-match check keeps this from misfiring on subtitles that merely
  // contain other text (e.g. a routine's recurrence line).
  const phoneValue = intent === 'phone' ? phoneValueFor(result) : undefined
  const subtitleIsPhoneLink = Boolean(phoneValue && result.subtitle === phoneValue)

  // A native <button> can't legally contain the <a> the phone link needs, so
  // this is a div with role="button" instead — Enter/Space wired up to match.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      className={`
        w-full flex items-center gap-3 px-3 py-2 text-left rounded-lg transition-colors cursor-pointer
        ${isSelected
          ? 'bg-primary-50 text-primary-900'
          : 'hover:bg-neutral-50'
        }
        ${result.completed ? 'opacity-60' : ''}
      `}
    >
      {renderIcon()}
      <div className="flex-1 min-w-0">
        <div
          className={`
            text-sm font-medium truncate
            ${result.completed ? 'line-through text-neutral-400' : 'text-neutral-800'}
          `}
        >
          {result.title}
        </div>
        {result.subtitle && (
          <div className="text-xs text-neutral-500 truncate">
            {subtitleIsPhoneLink ? (
              <a
                href={`tel:${phoneValue!.replace(/[^\d+]/g, '')}`}
                onClick={(e) => e.stopPropagation()}
                className="hover:underline"
              >
                {result.subtitle}
              </a>
            ) : (
              result.subtitle
            )}
          </div>
        )}
      </div>
    </div>
  )
}
